import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import MerchantLayout from '@/components/layouts/MerchantLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Download, Loader2, Check, Code, FileCode, Key, Package, Puzzle, ShieldCheck, Zap, Globe, RefreshCw, Server } from 'lucide-react';

const FRAMEWORKS = [
  { id: 'php',     name: 'PHP Core',        description: 'Vanilla PHP with cURL',          icon: '<?>' },
  { id: 'laravel', name: 'Laravel',         description: 'Laravel MVC + Blade templates',  icon: 'La'  },
  { id: 'nodejs',  name: 'Node.js Express', description: 'Express.js with axios',          icon: 'JS'  },
];

// Platform plugins — all auto-set callback URLs
const PLATFORMS = [
  {
    id: 'wordpress-plugin', label: 'WordPress + WooCommerce', badge: 'PHP Plugin',
    badgeClass: 'bg-primary/10 text-primary',
    icon: 'WP', version: 'WordPress 5.6+ · PHP 7.4+',
    file: 'coinpayup-crypto-payments.zip',
    description: 'Plug & Play WordPress plugin. Supports shortcodes on any page and full WooCommerce gateway. Per-order callback URLs auto-set.',
    features: [
      { icon: Zap,         text: 'Plug & Play — just enter your API key' },
      { icon: Puzzle,      text: 'WooCommerce gateway (auto order status updates)' },
      { icon: Code,        text: 'Shortcode: [coinpayup_payment amount="25" currency="USDT"]' },
      { icon: ShieldCheck, text: 'Webhook HMAC-SHA256 signature verification' },
    ],
  },
  {
    id: 'shopify-app', label: 'Shopify', badge: 'Node.js App',
    badgeClass: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    icon: 'SF', version: 'Node.js 16+',
    file: 'coinpayup-shopify-app.zip',
    description: 'Self-hosted Node.js app. Installs via OAuth, registers as payment gateway, per-order URLs auto-built.',
    features: [
      { icon: Zap,         text: 'OAuth install — one URL to set up' },
      { icon: RefreshCw,   text: 'Per-order callback URLs auto-built' },
      { icon: ShieldCheck, text: 'Shopify HMAC + CoinPayUp webhook verification' },
      { icon: Globe,       text: 'Auto order status: Pending → Paid → Complete' },
    ],
  },
  {
    id: 'magento-plugin', label: 'Magento 2', badge: 'PHP Module',
    badgeClass: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
    icon: 'M2', version: 'Magento 2.4+ · PHP 7.4+',
    file: 'coinpayup-magento2.zip',
    description: 'Magento 2 payment module. Installs via Composer or manually. Webhook HMAC verification and per-order redirect URLs.',
    features: [
      { icon: Package,     text: 'Installs via Composer or manual upload' },
      { icon: Zap,         text: 'Admin config: API key, currency, order status' },
      { icon: RefreshCw,   text: 'Per-order success/cancel URLs auto-generated' },
      { icon: ShieldCheck, text: 'Webhook HMAC-SHA256 verification' },
    ],
  },
  {
    id: 'prestashop-plugin', label: 'PrestaShop', badge: 'PHP Module',
    badgeClass: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    icon: 'PS', version: 'PrestaShop 1.7+ · PHP 7.4+',
    file: 'coinpayup-prestashop.zip',
    description: 'PrestaShop payment module. Upload and install from Module Manager. Webhook and callback URLs auto-registered on save.',
    features: [
      { icon: Package,     text: 'Upload & install from Module Manager' },
      { icon: Zap,         text: 'Config via Back Office — no code editing' },
      { icon: RefreshCw,   text: 'Callback URLs auto-built per order' },
      { icon: ShieldCheck, text: 'Webhook HMAC verification' },
    ],
  },
  {
    id: 'opencart-plugin', label: 'OpenCart', badge: 'PHP Extension',
    badgeClass: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
    icon: 'OC', version: 'OpenCart 4.x · PHP 8.0+',
    file: 'coinpayup-opencart.zip',
    description: 'OpenCart 4.x payment extension. Upload files, install from Extensions panel, configure API key. Webhook auto-registered.',
    features: [
      { icon: Package,     text: 'Upload & install from Extensions → Payments' },
      { icon: Zap,         text: 'Admin settings: enable, API key, currency' },
      { icon: RefreshCw,   text: 'Per-order callback URLs auto-built' },
      { icon: ShieldCheck, text: 'Webhook HMAC verification' },
    ],
  },
  {
    id: 'bigcommerce-app', label: 'BigCommerce', badge: 'Node.js App',
    badgeClass: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
    icon: 'BC', version: 'Node.js 16+',
    file: 'coinpayup-bigcommerce-app.zip',
    description: 'Self-hosted Node.js app for BigCommerce. OAuth install, auto-webhook registration, per-order payment links sent to customers.',
    features: [
      { icon: Zap,         text: 'OAuth install via BigCommerce Dev Tools' },
      { icon: Globe,       text: 'Auto payment link sent to customer on order' },
      { icon: RefreshCw,   text: 'Per-order callback URLs auto-built' },
      { icon: ShieldCheck, text: 'Webhook HMAC verification' },
    ],
  },
];

const DemoDownloads = () => {
  const [downloading, setDownloading] = useState({});
  const [apiKey, setApiKey] = useState('');
  const [selectedKeyId, setSelectedKeyId] = useState(null);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api-keys`).then(r => setApiKeys(r.data)).catch(() => {});
  }, []);

  const downloadZip = async (endpointId, filename) => {
    setDownloading(d => ({ ...d, [endpointId]: true }));
    try {
      const response = await axios.get(`${API}/demo-downloads/${endpointId}`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`${filename} downloaded!`);
    } catch {
      toast.error('Download failed. Please try again.');
    } finally {
      setDownloading(d => ({ ...d, [endpointId]: false }));
    }
  };

  const handleGenerate = async () => {
    if (!apiKey.trim() && !selectedKeyId) { toast.error('Please enter or select your API key'); return; }
    if (!selectedFramework) { toast.error('Please select a framework'); return; }
    setGenerating(true);
    try {
      const payload = { framework: selectedFramework };
      if (selectedKeyId) payload.api_key_id = selectedKeyId;
      else payload.api_key = apiKey.trim();
      const response = await axios.post(`${API}/demo-downloads/generate`, payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `coinpayup-demo-${selectedFramework}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Demo project downloaded!');
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try { toast.error(JSON.parse(text).detail || 'Generation failed'); } catch { toast.error('Generation failed'); }
      } else {
        toast.error(err.response?.data?.detail || 'Generation failed');
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <MerchantLayout title="Demo Downloads">
      <div className="space-y-8 max-w-4xl" data-testid="demo-downloads-page">
        <p className="text-muted-foreground">Download production-ready integration packages for CoinPayUp. All auto-configure callback URLs — no manual setup in your merchant account needed.</p>

        {/* ── Platform Plugins Grid ─────────────────────────── */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Server className="h-5 w-5 text-primary" />
            <h2 className="text-base font-semibold">Platform Plugins & Apps</h2>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {PLATFORMS.map((p) => (
              <Card key={p.id} className="border-border bg-card" data-testid={`platform-${p.id}`}>
                <CardContent className="pt-5">
                  <div className="flex flex-col sm:flex-row gap-5">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold">{p.label}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.badgeClass}`}>{p.badge}</span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">{p.description}</p>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-1">
                        {p.features.map(({ icon: Icon, text }) => (
                          <li key={text} className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Icon className="h-3.5 w-3.5 text-primary shrink-0" />
                            <span>{text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex flex-row sm:flex-col items-center gap-3 sm:w-36 shrink-0">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-base font-bold text-primary shrink-0">{p.icon}</div>
                      <div className="flex-1 sm:flex-none sm:w-full">
                        <Button
                          size="sm"
                          className="w-full"
                          onClick={() => downloadZip(p.id, p.file)}
                          disabled={!!downloading[p.id]}
                          data-testid={`download-${p.id}-btn`}
                        >
                          {downloading[p.id]
                            ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />Downloading...</>
                            : <><Download className="h-3.5 w-3.5 mr-1.5" />Download .zip</>
                          }
                        </Button>
                        <p className="text-xs text-muted-foreground text-center mt-1.5">{p.version}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* ── Divider ─────────────────────────────────────────── */}
        <div className="flex items-center gap-3">
          <div className="flex-1 border-t border-border" />
          <span className="text-xs text-muted-foreground font-medium">CODE DEMOS (API KEY REQUIRED)</span>
          <div className="flex-1 border-t border-border" />
        </div>

        {/* ── Step 1: API Key ─────────────────────────────────── */}
        <Card className="border-border bg-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Key className="h-5 w-5 text-primary" />
              <h3 className="text-base font-semibold">1. Enter your API Key</h3>
            </div>
            <Input
              placeholder="Your CoinPayUp API key"
              value={apiKey}
              onChange={(e) => { setApiKey(e.target.value); setSelectedKeyId(null); }}
              className="font-mono text-sm mb-3"
              data-testid="demo-api-key-input"
            />
            {apiKeys.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground">Or select from your existing keys:</p>
                {apiKeys.map((k) => (
                  <button key={k.id} onClick={() => { setSelectedKeyId(k.id); setApiKey(k.name); toast.success(`API key "${k.name}" selected`); }}
                    className={`flex items-center gap-2 text-xs transition-colors ${selectedKeyId === k.id ? 'text-primary font-medium' : 'text-muted-foreground hover:text-primary'}`}
                    data-testid={`select-key-${k.id}`}>
                    {selectedKeyId === k.id ? <Check className="h-3 w-3" /> : <Code className="h-3 w-3" />}
                    <span className="font-mono">{k.name}</span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Step 2: Framework ───────────────────────────────── */}
        <Card className="border-border bg-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <FileCode className="h-5 w-5 text-primary" />
              <h3 className="text-base font-semibold">2. Select Framework</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {FRAMEWORKS.map((fw) => (
                <button key={fw.id} onClick={() => setSelectedFramework(fw.id)}
                  className={`flex items-center gap-3 p-4 rounded-xl border text-left transition-all ${selectedFramework === fw.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`}
                  data-testid={`framework-${fw.id}`}>
                  <div className={`w-11 h-11 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${selectedFramework === fw.id ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>{fw.icon}</div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm flex items-center gap-1.5">{fw.name}{selectedFramework === fw.id && <Check className="h-3.5 w-3.5 text-primary" />}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{fw.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* ── Generate Button ─────────────────────────────────── */}
        <Button className="w-full h-12 text-base font-semibold"
          disabled={(!apiKey.trim() && !selectedKeyId) || !selectedFramework || generating}
          onClick={handleGenerate} data-testid="generate-demo-btn">
          {generating ? <><Loader2 className="h-5 w-5 animate-spin mr-2" />Generating...</> : <><Download className="h-5 w-5 mr-2" />Download Demo Project</>}
        </Button>
      </div>
    </MerchantLayout>
  );
};

export default DemoDownloads;
